export class StringUtilities {

    // The Basic Email Blast template injects sender-supplied text raw into the email HTML via a literal
    // string replace (no escaping, no newline handling). So we escape HTML-significant characters here to
    // keep the markup intact, then turn user line breaks into <br/> so they actually render in the email.
    public static escapeHtmlWithLineBreaks(input: string | null | undefined): string {
        if (!input) {
            return '';
        }

        const escaped = input
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
            .replace(/'/g, '&#39;');

        return escaped.replace(/\r\n|\r|\n/g, '<br/>');
    }

    // Light client-side scrub for HTML produced by the rich-text editor before we hand it to the API.
    // Server-side sanitization is still the authoritative defense; this just strips the tags and attributes
    // that are dangerous enough that we don't want them leaving the browser under any circumstances.
    private static readonly forbiddenTags = new Set(
        ['SCRIPT', 'IFRAME', 'OBJECT', 'EMBED', 'STYLE', 'LINK', 'META', 'BASE', 'FORM']
    );
    private static readonly urlAttributes = new Set(['href', 'src', 'action', 'formaction']);
    private static readonly dangerousUrlScheme = /^\s*(javascript|vbscript)\s*:/i;

    public static sanitizeEmailBody(html: string | null | undefined): string {
        if (!html) {
            return '';
        }

        const doc = new DOMParser().parseFromString(html, 'text/html');
        const toRemove: Element[] = [];
        const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_ELEMENT);
        let node = walker.nextNode() as Element | null;

        while (node) {
            if (this.forbiddenTags.has(node.tagName)) {
                toRemove.push(node);
            } else {
                for (const attr of Array.from(node.attributes)) {
                    const name = attr.name.toLowerCase();
                    if (name.startsWith('on')) {
                        node.removeAttribute(attr.name);
                        continue;
                    }
                    if (this.urlAttributes.has(name) && this.dangerousUrlScheme.test(attr.value)) {
                        node.removeAttribute(attr.name);
                    }
                }
            }
            node = walker.nextNode() as Element | null;
        }

        for (const element of toRemove) {
            element.remove();
        }

        return doc.body.innerHTML;
    }
}
